import { twMerge } from 'tailwind-merge';
import { mapToKey } from '@witmetrics/utils/arrays';
import {
  useDispatch,
  useNotesFromFileId,
  usePracticeUsers,
} from '@witmetrics/data-store';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import Card from '@/components/Card';
import NotesField from '@/components/inputs/NotesField';
import NotesTimeline from '@/components/NotesTimeline';
import Title from '@/components/Title';
import Button from '@/components/Button';
import { classes, createFileNote } from './utils';

export type FileTimelineProps = {
  className?: string;
  fileId: number;
};

export default function FileTimeline({ className, fileId }: FileTimelineProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const notes = useNotesFromFileId(fileId);
  const practiceUsers = usePracticeUsers();
  const [isNewNoteShown, toggleNewNote] = useToggle();
  const [isSavingNote, toggleSavingNote] = useToggle();

  const handleSaveNote = async (content: string) => {
    try {
      toggleSavingNote(true);
      dispatch.addFileNotes([await createFileNote(fileId, content)]);
      toggleSavingNote(false);
      toggleNewNote(false);
    } catch (err) {
      toggleSavingNote(false);
      onApiError(err, 'Error saving note');
    }
  };

  return (
    <Card className={twMerge(classes.card, className)}>
      <div className={classes.titleWrapper}>
        <Title size="xs">Notes</Title>
        <Button
          className={classes.addNoteButton}
          variant="ghost"
          onClick={() => toggleNewNote(true)}>
          Add Note
        </Button>
      </div>
      {isNewNoteShown && (
        <div className={classes.newNoteWrapper}>
          <NotesField
            isSaving={isSavingNote}
            users={mapToKey(practiceUsers, 'user')}
            onSave={handleSaveNote}
            onCancel={() => toggleNewNote(false)}
          />
        </div>
      )}
      {isEmpty(notes) ? (
        <div className={classes.message}>No notes for current file.</div>
      ) : (
        <NotesTimeline
          className={classes.timeline}
          variant="aurora"
          notes={notes}
        />
      )}
    </Card>
  );
}
